(() => {
  const selector = '[data-product-description-toggle]';
  const mobileMediaQuery = window.matchMedia('(max-width: 767px)');
  const resizeObservers = new WeakMap();
  const accordionSyncFrames = new WeakMap();
  let resizeFrame;

  const collapsedHeight = () => (mobileMediaQuery.matches ? 280 : 340);

  const setLabel = (wrapper, expanded) => {
    const more = wrapper.querySelector('[data-description-more]');
    const less = wrapper.querySelector('[data-description-less]');

    if (more) more.hidden = expanded;
    if (less) less.hidden = !expanded;
  };

  const descriptionTop = (wrapper) => {
    const content = wrapper.querySelector('[data-product-description-content]') || wrapper;
    return Math.max(0, content.getBoundingClientRect().top + window.scrollY - 24);
  };

  const scrollToDescriptionStart = (top) => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    window.scrollTo({
      top,
      behavior: prefersReducedMotion ? 'auto' : 'smooth'
    });
  };

  const syncMobileAccordionHeight = (wrapper) => {
    if (!mobileMediaQuery.matches) return;

    const accordionItem = wrapper.closest('.m-accordion--item');
    const accordionContent = wrapper.closest('.m-accordion--item-content');

    if (!accordionItem || !accordionContent || !accordionItem.classList.contains('open')) return;

    const pendingFrame = accordionSyncFrames.get(wrapper);
    if (pendingFrame) cancelAnimationFrame(pendingFrame);

    const frame = requestAnimationFrame(() => {
      const contentHeight = accordionContent.scrollHeight;

      if (contentHeight > 0) {
        accordionItem.style.setProperty('--content-max-height', `${contentHeight}px`);
      }

      accordionSyncFrames.delete(wrapper);
    });

    accordionSyncFrames.set(wrapper, frame);
  };

  const watchWrapperSize = (wrapper) => {
    if (!('ResizeObserver' in window) || resizeObservers.has(wrapper)) return;

    const observer = new ResizeObserver(() => {
      syncMobileAccordionHeight(wrapper);
    });

    observer.observe(wrapper);
    resizeObservers.set(wrapper, observer);
  };

  const refresh = (wrapper) => {
    const content = wrapper.querySelector('[data-product-description-content]');
    const button = wrapper.querySelector('[data-product-description-button]');

    if (!content || !button) return;

    const toggleTolerance = mobileMediaQuery.matches ? 70 : 80;
    const needsToggle = content.scrollHeight > collapsedHeight() + toggleTolerance;
    wrapper.classList.toggle('is-collapsible', needsToggle);
    button.hidden = !needsToggle;

    if (!needsToggle) {
      wrapper.classList.remove('is-expanded');
      button.setAttribute('aria-expanded', 'false');
      setLabel(wrapper, false);
    }

    syncMobileAccordionHeight(wrapper);
  };

  const initOne = (wrapper) => {
    if (wrapper.dataset.descriptionToggleInitialized === 'true') return;

    const button = wrapper.querySelector('[data-product-description-button]');
    if (!button) return;

    wrapper.dataset.descriptionToggleInitialized = 'true';
    watchWrapperSize(wrapper);

    button.addEventListener('click', () => {
      const wasExpanded = wrapper.classList.contains('is-expanded');
      const targetTop = wasExpanded ? descriptionTop(wrapper) : null;
      const expanded = wrapper.classList.toggle('is-expanded');

      button.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      setLabel(wrapper, expanded);

      syncMobileAccordionHeight(wrapper);
      requestAnimationFrame(() => syncMobileAccordionHeight(wrapper));

      if (wasExpanded && !expanded && targetTop !== null) {
        requestAnimationFrame(() => scrollToDescriptionStart(targetTop));
      }
    });

    refresh(wrapper);
    requestAnimationFrame(() => refresh(wrapper));
  };

  const initAll = (root = document) => {
    if (root.matches && root.matches(selector)) initOne(root);
    if (root.querySelectorAll) root.querySelectorAll(selector).forEach(initOne);
  };

  const refreshAll = () => {
    document.querySelectorAll(selector).forEach(refresh);
  };

  const disconnectAll = (root) => {
    if (!root || !root.querySelectorAll) return;

    root.querySelectorAll(selector).forEach((wrapper) => {
      const observer = resizeObservers.get(wrapper);
      if (observer) observer.disconnect();
      resizeObservers.delete(wrapper);

      const pendingFrame = accordionSyncFrames.get(wrapper);
      if (pendingFrame) cancelAnimationFrame(pendingFrame);
      accordionSyncFrames.delete(wrapper);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  window.addEventListener('load', refreshAll, { once: true });

  window.addEventListener('resize', () => {
    cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(refreshAll);
  });

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => disconnectAll(event.target));
})();
